import EditNoteIcon from "@mui/icons-material/EditNote";
import TuneIcon from "@mui/icons-material/Tune";
import { Box } from "@mui/material";
import { type FC, type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemPageHeader } from "../../../components/UIToolkit/molecules/SystemPageHeader";
import type { DialogCheckedState } from "../../../components/UIToolkit/Popups/Dialog";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import type { PbjJob, PbjJobsSaveBody } from "../../../utils/models";
import { usePbjJobMapping, usePbjJobs, useSavePbjJobs } from "../hooks/usePbjJobs";
import { JobMappingGrid, type MappingGridRow } from "./JobMappingGrid";
import { type JobDraft, ManageJobsDialog } from "./ManageJobsDialog";

const toDraft = (job: PbjJob): JobDraft => ({ key: job.id, id: job.id, code: job.code, description: job.description });

type Props = {
	// Wraps a "Period & facility" header button in the page-level period/facility popover. The date
	// controls are inert here (Jobs is dept-config, not date-scoped), but facility + Calculate apply.
	renderChangePeriod: (trigger: ReactElement) => ReactNode;
};

/**
 * "Jobs" tab — edit the current department's role_string → PBJ job mapping and CRUD its job registry.
 * Edits buffer locally; the Save button opens a confirmation that can fan the whole changeset out to
 * every department in the network (department.hospital) or apply it to the current department only.
 */
export const JobsTab: FC<Props> = ({ renderChangePeriod }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const deptId = chosenDepartment.department_id;

	const mappingQuery = usePbjJobMapping(deptId);
	const jobsQuery = usePbjJobs(deptId);
	const saveMutation = useSavePbjJobs(deptId);

	// Local working state: the job registry draft + per-role mapping overrides (roleString → code|null).
	const [draftJobs, setDraftJobs] = useState<JobDraft[]>([]);
	const [pendingMapping, setPendingMapping] = useState<Map<string, string | null>>(new Map());
	const [manageOpen, setManageOpen] = useState(false);
	// The registry as it stood when Manage Jobs opened — Cancel restores it.
	const [jobsBeforeManage, setJobsBeforeManage] = useState<JobDraft[]>([]);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const serverJobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);

	// Re-seed the draft whenever the loaded registry changes (incl. dept switch + post-save refresh),
	// dropping any stale pending edits.
	useEffect(() => {
		setDraftJobs(serverJobs.map(toDraft));
		setPendingMapping(new Map());
	}, [serverJobs]);

	useEffect(() => {
		track("pbj_jobs_tab_viewed", { department_id: deptId });
	}, [deptId, track]);

	// Effective mapping code for a role: pending override wins over the server value.
	const effectiveCode = useCallback(
		(roleString: string, serverCode: string | null): string => {
			if (pendingMapping.has(roleString)) return pendingMapping.get(roleString) ?? "";
			return serverCode ?? "";
		},
		[pendingMapping],
	);

	const mappingRows: MappingGridRow[] = useMemo(
		() => (mappingQuery.data ?? []).map((m) => ({ roleString: m.roleString, code: effectiveCode(m.roleString, m.code) })),
		[mappingQuery.data, effectiveCode],
	);

	// Dropdown source: the effective registry (drafts with a code), labeled "{code} - {description}".
	const jobOptions = useMemo(
		() => draftJobs.filter((j) => j.code.trim() !== "").map((j) => ({ value: j.code, label: `${j.code} - ${j.description}` })),
		[draftJobs],
	);

	const rolesReferencingCode = useCallback((code: string): string[] => mappingRows.filter((r) => r.code === code).map((r) => r.roleString), [mappingRows]);

	const handleMappingChange = useCallback((roleString: string, code: string | null) => {
		setPendingMapping((prev) => new Map(prev).set(roleString, code));
	}, []);

	// Dirty = any pending mapping override, or the job draft diverges from the server registry.
	const jobsDirty = useMemo(() => {
		const serverById = new Map(serverJobs.map((j) => [j.id, j]));
		if (draftJobs.filter((d) => d.id).length !== serverJobs.length) return true; // an add or delete
		return draftJobs.some((d) => {
			if (!d.id) return true;
			const original = serverById.get(d.id);
			return !original || original.code !== d.code.trim() || original.description !== d.description;
		});
	}, [draftJobs, serverJobs]);

	const isDirty = pendingMapping.size > 0 || jobsDirty;

	const buildChangeset = useCallback(
		(applyToNetwork: boolean): PbjJobsSaveBody => {
			const serverById = new Map(serverJobs.map((j) => [j.id, j]));
			const jobUpserts = draftJobs
				.filter((d) => {
					if (!d.id) return true; // new
					const original = serverById.get(d.id);
					return !original || original.code !== d.code.trim() || original.description !== d.description;
				})
				.map((d) => ({ id: d.id, tempId: d.tempId, code: d.code.trim(), description: d.description }));

			const draftIds = new Set(draftJobs.map((d) => d.id).filter(Boolean));
			const jobDeletes = serverJobs.filter((j) => !draftIds.has(j.id)).map((j) => ({ id: j.id }));

			const mappingEdits = Array.from(pendingMapping.entries()).map(([roleString, code]) => ({ roleString, code }));

			return { departmentId: deptId, applyToNetwork, jobUpserts, jobDeletes, mappingEdits };
		},
		[deptId, draftJobs, serverJobs, pendingMapping],
	);

	const handleConfirmSave = useCallback(
		(checkedState?: DialogCheckedState[]) => {
			const applyToNetwork = checkedState?.[0]?.checked ?? false;
			saveMutation.mutate(buildChangeset(applyToNetwork), { onSuccess: () => setConfirmOpen(false) });
		},
		[buildChangeset, saveMutation],
	);

	const handleOpenManage = useCallback(() => {
		setJobsBeforeManage(draftJobs);
		setManageOpen(true);
		track("pbj_jobs_manage_opened", { department_id: deptId });
	}, [draftJobs, deptId, track]);

	const handleCancelManage = useCallback(() => {
		setDraftJobs(jobsBeforeManage);
		setManageOpen(false);
	}, [jobsBeforeManage]);

	const handleSaveFromManage = useCallback(() => {
		setManageOpen(false);
		setConfirmOpen(true);
	}, []);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, height: "100%", minHeight: 0 }} data-testid="pbj-jobs-tab">
			<SystemPageHeader
				dataTestId="pbj-jobs-header"
				eyebrow={t("pbj_model.tabs.jobs")}
				title={t("pbj_model.jobs.title")}
				subtitle={t("pbj_model.jobs.subtitle")}
				actions={
					<>
						{renderChangePeriod(
							<SystemButton buttonVariant="secondary" size="small" leadingIcon={<TuneIcon sx={{ fontSize: 16 }} />} noFullWidthMobile>
								{t("pbj_model.hero.change_period")}
							</SystemButton>,
						)}
						<SystemButton
							buttonVariant="secondary"
							size="small"
							onClick={handleOpenManage}
							leadingIcon={<EditNoteIcon sx={{ fontSize: 18 }} />}
							noFullWidthMobile
							data-testid="pbj-jobs-manage-button"
						>
							{t("pbj_model.jobs.manage_jobs")}
						</SystemButton>
						<SystemButton
							buttonVariant={isDirty ? "primary" : "secondary"}
							size="small"
							onClick={() => setConfirmOpen(true)}
							disabled={!isDirty}
							loading={saveMutation.isPending}
							noFullWidthMobile
							data-testid="pbj-jobs-save-button"
						>
							{t("pbj_model.jobs.save")}
						</SystemButton>
					</>
				}
			/>

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<JobMappingGrid
					rows={mappingRows}
					jobOptions={jobOptions}
					onMappingChange={handleMappingChange}
					loading={mappingQuery.isLoading || jobsQuery.isLoading}
					error={mappingQuery.isError || jobsQuery.isError}
				/>
			</Box>

			<ManageJobsDialog
				open={manageOpen}
				onCancel={handleCancelManage}
				onSave={handleSaveFromManage}
				hasChanges={isDirty}
				jobs={draftJobs}
				onChange={setDraftJobs}
				rolesReferencingCode={rolesReferencingCode}
			/>

			<SystemDialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				title={t("pbj_model.jobs.save_confirm.title")}
				body={t("pbj_model.jobs.save_confirm.body")}
				confirm={t("pbj_model.jobs.save_confirm.apply")}
				onConfirm={handleConfirmSave}
				confirmLoading={saveMutation.isPending}
				checkboxes={[{ label: t("pbj_model.jobs.save_confirm.network"), required: false, defaultValue: false }]}
				dataTestId="pbj-jobs-save-confirm"
			/>
		</Box>
	);
};
